import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import ImageDropzone from "@/components/admin/ImageDropzone";
import AiImproveButton from "@/components/admin/AiImproveButton";
import UpgradeModal from "@/components/site/UpgradeModal";
import LanguageSwitcher from "@/components/site/LanguageSwitcher";
import { BUSINESS_CATEGORIES, ACCENT_PRESETS } from "@/lib/business";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import {
    Save, ExternalLink, Loader2, Plus, Trash2, Eye, EyeOff, LogOut, Check,
    Copy, Download, MessageCircle, BarChart3, Star, X, LayoutGrid,
    Info, Users, Phone, Package, Wrench, Images, ShieldCheck, Share2, Crown,
} from "lucide-react";

const uid = () => "x_" + Math.random().toString(36).slice(2);
const FREE_PRODUCT_LIMIT = 5;
const PAID_PLANS = ["pro", "business"];

const TAB_META = [
    { key: "overview", icon: LayoutGrid,  label: "tab_overview" },
    { key: "info",     icon: Info,        label: "tab_info" },
    { key: "about",    icon: Users,       label: "tab_about" },
    { key: "contact",  icon: Phone,       label: "tab_contact" },
    { key: "products", icon: Package,     label: "tab_products" },
    { key: "services", icon: Wrench,      label: "tab_services" },
    { key: "gallery",  icon: Images,      label: "tab_gallery" },
    { key: "reviews",  icon: Star,        label: "tab_reviews" },
    { key: "seo",      icon: ShieldCheck, label: "tab_seo" },
    { key: "share",    icon: Share2,      label: "tab_share" },
];

export default function DashboardPage() {
    const navigate = useNavigate();
    const { user, logout } = useApp();
    const { t } = useI18n();
    const [params] = useSearchParams();
    const [biz, setBiz] = useState(null);
    const [form, setForm] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState("overview");
    const [showReady, setShowReady] = useState(params.get("ready") === "1");
    const [showUpgrade, setShowUpgrade] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const b = (await api.get("/my/business")).data;
                if (!b) { navigate("/onboarding"); return; }
                setBiz(b); setForm(b);
            } catch (e) { toast.error(formatApiError(e)); }
            finally { setLoading(false); }
        })();
    }, [navigate]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const save = async (patch = {}, silent = false) => {
        setSaving(true);
        try {
            const payload = { ...form, ...patch };
            const { id, user_id, stats, created_at, updated_at, ...body } = payload;
            const updated = (await api.put(`/my/business/${biz.id}`, body)).data;
            setBiz(updated); setForm(updated);
            if (!silent) toast.success(t("saved"));
            return updated;
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const togglePublish = async () => {
        await save({ published: !form.published }, true);
        toast.success(!form.published ? t("live_banner_ready") : t("draft"));
    };

    const pickTab = (key) => { setTab(key); };

    if (loading || !form) {
        return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-accent" /></div>;
    }

    const liveUrl = `${window.location.origin}/b/${biz.slug}`;
    const isPro = PAID_PLANS.includes(biz.plan);
    const productCount = (form.products || []).filter((p) => p.name).length;

    const Sidebar = ({ onPick }) => (
        <aside className="w-full lg:w-56 shrink-0" data-testid="dashboard-sidebar">
            <nav className="glass rounded-2xl p-2 flex flex-col gap-0.5">
                {TAB_META.map(({ key, icon: Icon, label }) => (
                    <button
                        key={key}
                        onClick={() => onPick(key)}
                        data-testid={`tab-${key}`}
                        className={`w-full flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm text-left transition-all ${
                            tab === key
                                ? "bg-foreground text-background shadow-sm"
                                : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                        }`}
                    >
                        <Icon className="w-4 h-4 shrink-0" />
                        <span className="truncate">{t(label)}</span>
                    </button>
                ))}
            </nav>
        </aside>
    );

    return (
        <div className="relative min-h-screen pb-20">
            {/* Top bar */}
            <header className="sticky top-0 z-40 glass border-b border-border/60">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
                    <Link to="/" className="flex items-center gap-2.5 min-w-0">
                        <div className="w-7 h-7 rounded-full sphere shrink-0" />
                        <span className="font-heading text-sm font-semibold uppercase tracking-[0.18em] truncate">Bizlinkk</span>
                    </Link>
                    <div className="flex items-center gap-2">
                        <LanguageSwitcher variant="ghost" testid="dash-lang" />
                        <a href={liveUrl} target="_blank" rel="noreferrer">
                            <Button variant="ghost" size="sm" className="rounded-full glass" data-testid="view-website-btn"><ExternalLink className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">{t("view_site")}</span></Button>
                        </a>
                        <Button size="sm" onClick={() => save()} disabled={saving} className="btn-accent rounded-full" data-testid="save-btn">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /><span>{t("save")}</span></>}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { logout(); navigate("/"); }} className="rounded-full" data-testid="logout-btn"><LogOut className="w-4 h-4" /></Button>
                    </div>
                </div>
            </header>

            <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
                {showReady && (
                    <div className="glass-strong rounded-2xl p-5 mb-5 flex items-start gap-3 fade-up" data-testid="ready-banner">
                        <div className="w-10 h-10 rounded-full bg-emerald-500/15 flex items-center justify-center shrink-0"><Check className="w-5 h-5 text-emerald-600" /></div>
                        <div className="flex-1">
                            <div className="font-heading font-semibold">{t("live_banner_ready")}</div>
                            <p className="text-sm text-muted-foreground mt-0.5">{t("live_banner_sub")}</p>
                        </div>
                        <button onClick={() => setShowReady(false)} className="text-muted-foreground"><X className="w-4 h-4" /></button>
                    </div>
                )}

                {/* Publish status */}
                <div className="glass rounded-2xl p-4 mb-5 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0 flex-wrap">
                        <span className={`inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1 ${form.published ? "bg-emerald-500/15 text-emerald-700" : "bg-amber-500/15 text-amber-700"}`}>
                            {form.published ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                            {form.published ? t("live") : t("draft")}
                        </span>
                        <span data-testid="plan-badge" className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1 ${isPro ? "bg-accent/15 text-accent" : "bg-secondary text-muted-foreground"}`}>
                            {isPro && <Crown className="w-3.5 h-3.5" />}
                            {isPro ? "PRO" : "FREE"}
                            <span className="font-normal text-muted-foreground" data-testid="product-usage">
                                · {isPro ? "Unlimited products" : `${productCount} / ${FREE_PRODUCT_LIMIT} products used`}
                            </span>
                        </span>
                        <a href={liveUrl} target="_blank" rel="noreferrer" className="text-sm text-accent truncate max-w-[200px] sm:max-w-none" data-testid="live-url">{liveUrl}</a>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                        {!isPro && (
                            <Button size="sm" variant="ghost" onClick={() => setShowUpgrade(true)} className="rounded-full glass text-accent hover:text-accent" data-testid="upgrade-cta-top">
                                <Crown className="w-3.5 h-3.5 mr-1" /> Upgrade
                            </Button>
                        )}
                        <span className="text-muted-foreground">{form.published ? t("published") : t("publish")}</span>
                        <Switch checked={!!form.published} onCheckedChange={togglePublish} data-testid="publish-switch" />
                    </div>
                </div>

                <p className="text-xs text-muted-foreground -mt-3 mb-5 px-1 flex items-start gap-1.5" data-testid="save-hint">
                    <Info className="w-3.5 h-3.5 text-accent shrink-0 mt-0.5" /> {t("save_before_publish_note")}
                </p>

                <div className="flex flex-col lg:flex-row gap-5 pb-10">
                    <div className="lg:sticky lg:top-20 lg:self-start">
                        <Sidebar onPick={pickTab} />
                    </div>

                    <div className="flex-1 min-w-0" data-testid="dash-main">
                        {tab === "overview" && <Overview t={t} biz={biz} form={form} liveUrl={liveUrl} setTab={pickTab} />}
                        {tab === "info" && <InfoTab t={t} form={form} set={set} biz={biz} save={save} />}
                        {tab === "about" && <AboutTab t={t} form={form} set={set} />}
                        {tab === "contact" && <ContactTab t={t} form={form} set={set} />}
                        {tab === "products" && <ListTab t={t} kind="product" form={form} set={set} isPro={isPro} onReachLimit={() => setShowUpgrade(true)} />}
                        {tab === "services" && <ListTab t={t} kind="service" form={form} set={set} />}
                        {tab === "gallery" && <GalleryTab t={t} form={form} set={set} />}
                        {tab === "reviews" && <ReviewsTab t={t} form={form} set={set} />}
                        {tab === "seo" && <SeoTab t={t} form={form} set={set} />}
                        {tab === "share" && <ShareTab t={t} liveUrl={liveUrl} biz={biz} />}
                    </div>
                </div>
            </div>
            <UpgradeModal open={showUpgrade} onOpenChange={setShowUpgrade} />
        </div>
    );
}

function Card({ title, desc, children }) {
    return (
        <div className="glass rounded-2xl p-5 sm:p-6 mb-4">
            {title && <h3 className="font-heading text-lg font-semibold">{title}</h3>}
            {desc && <p className="text-sm text-muted-foreground mt-0.5 mb-4">{desc}</p>}
            {!desc && title && <div className="mb-4" />}
            {children}
        </div>
    );
}
function TextField({ label, value, onChange, placeholder, testid, type = "text" }) {
    return (
        <div>
            <Label className="text-sm">{label}</Label>
            <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-1 rounded-xl" data-testid={testid} />
        </div>
    );
}
function WhatsAppField({ label, value, onChange, testid }) {
    const local = String(value || "").replace(/^\+?91/, "").replace(/\D/g, "");
    return (
        <div>
            <Label className="text-sm">{label}</Label>
            <div className="mt-1 flex items-center rounded-xl border border-input bg-background px-3 h-10">
                <span className="text-sm text-muted-foreground select-none">+91</span>
                <input
                    inputMode="numeric"
                    value={local}
                    onChange={(e) => { const d = e.target.value.replace(/\D/g, "").slice(0, 10); onChange(d ? "91" + d : ""); }}
                    placeholder="90000 00000"
                    maxLength={10}
                    className="flex-1 bg-transparent outline-none px-2 text-sm"
                    data-testid={testid}
                />
            </div>
        </div>
    );
}
function AreaField({ label, value, onChange, placeholder, rows = 3, testid }) {
    return (
        <div>
            <Label className="text-sm">{label}</Label>
            <Textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={rows} className="mt-1 rounded-xl" data-testid={testid} />
        </div>
    );
}
function ImageField({ t, label, value, onChange, testid, ratio }) {
    const [url, setUrl] = useState("");
    return (
        <div>
            <Label className="text-sm">{label}</Label>
            {value ? (
                <div className="mt-2 relative rounded-xl overflow-hidden border border-border group" style={{ aspectRatio: ratio || "16/9" }}>
                    <img src={value} alt={label} className="w-full h-full object-cover" />
                    <button onClick={() => onChange("")} className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1.5" data-testid={`${testid}-remove`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
            ) : (
                <div className="mt-2 space-y-2">
                    <ImageDropzone compact onUploaded={onChange} testId={testid} cropToRatio={ratio === "1/1" ? 1 : undefined} />
                    <div className="flex gap-2">
                        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder={t("paste_image_url")} className="rounded-xl text-sm h-9" data-testid={`${testid}-url`} />
                        <Button type="button" variant="ghost" className="glass rounded-xl shrink-0 h-9" onClick={() => { if (url.trim()) { onChange(url.trim()); setUrl(""); } }} data-testid={`${testid}-url-add`}>{t("add")}</Button>
                    </div>
                </div>
            )}
        </div>
    );
}

function Overview({ t, biz, form, liveUrl, setTab }) {
    const s = biz.stats || {};
    const metrics = [
        { label: t("metric_views"),    value: s.views || 0 },
        { label: t("metric_whatsapp"), value: s.whatsapp || 0 },
        { label: t("metric_call"),     value: s.call || 0 },
        { label: t("metric_map"),      value: s.map || 0 },
        { label: t("metric_product"),  value: s.product || 0 },
        { label: t("metric_share"),    value: s.share || 0 },
    ];
    return (
        <div>
            <Card title={`${t("welcome")}, ${biz.name}`} desc={form.published ? t("overview_sub_live") : t("overview_sub_draft")}>
                <div className="flex flex-wrap gap-3">
                    <a href={liveUrl} target="_blank" rel="noreferrer"><Button size="lg" className="btn-accent rounded-full text-base px-7 h-12" data-testid="ov-view-btn"><ExternalLink className="w-5 h-5 mr-2" /> {t("view_site")}</Button></a>
                    <Button variant="ghost" size="lg" className="glass rounded-full text-base px-7 h-12" onClick={() => setTab("share")} data-testid="ov-share-btn"><Copy className="w-5 h-5 mr-2" /> {t("share_link")}</Button>
                </div>
            </Card>
            <Card title={t("visitor_analytics")} desc={t("visitor_analytics_sub")}>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {metrics.map((m) => (
                        <div key={m.label} className="rounded-xl bg-secondary/50 p-4" data-testid={`metric-${m.label}`}>
                            <div className="flex items-center gap-2 text-muted-foreground"><BarChart3 className="w-4 h-4" /><span className="text-xs">{m.label}</span></div>
                            <div className="font-heading text-2xl font-semibold mt-1">{m.value}</div>
                        </div>
                    ))}
                </div>
            </Card>
        </div>
    );
}

function InfoTab({ t, form, set, biz, save }) {
    const [slug, setSlug] = useState(biz.slug);
    const [slugState, setSlugState] = useState(null);
    const checkSlug = async () => {
        try {
            const { data } = await api.post("/my/business/check-slug", { slug, business_id: biz.id });
            setSlugState(data);
            if (data.available) { set("slug", data.slug); }
        } catch (e) { toast.error(formatApiError(e)); }
    };
    return (
        <div>
            <Card title={t("section_info")}>
                <div className="grid sm:grid-cols-2 gap-4">
                    <TextField label={t("field_business_name")} value={form.name} onChange={(v) => set("name", v)} testid="info-name" />
                    <div>
                        <Label className="text-sm">{t("field_category")}</Label>
                        <select value={form.category} onChange={(e) => set("category", e.target.value)} className="mt-1 w-full rounded-xl border border-input bg-background h-10 px-3 text-sm" data-testid="info-category">
                            {BUSINESS_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                </div>
                <div className="mt-4">
                    <Label className="text-sm">{t("field_slug")}</Label>
                    <div className="flex gap-2 mt-1">
                        <div className="flex-1 flex items-center rounded-xl border border-input bg-background px-3 text-sm">
                            <span className="text-muted-foreground">/b/</span>
                            <input value={slug} onChange={(e) => { setSlug(e.target.value); setSlugState(null); }} className="flex-1 bg-transparent outline-none py-2" data-testid="info-slug" />
                        </div>
                        <Button variant="ghost" className="glass rounded-xl" onClick={checkSlug} data-testid="info-slug-check">{t("slug_check")}</Button>
                    </div>
                    {slugState && <p className={`text-xs mt-1.5 ${slugState.available ? "text-emerald-600" : "text-destructive"}`}>{slugState.available ? t("slug_available") : (slugState.reason || t("slug_taken"))}</p>}
                </div>
                <div className="mt-4">
                    <TextField label={t("field_tagline")} value={form.tagline} onChange={(v) => set("tagline", v)} placeholder="Quality clothing for the whole family." testid="info-tagline" />
                    <AiImproveButton text={form.tagline} field="tagline" onImproved={(v) => set("tagline", v)} testid="tagline-ai-improve" />
                </div>
                <div className="mt-4">
                    <TextField label={t("field_short_desc")} value={form.description} onChange={(v) => set("description", v)} testid="info-description" />
                    <AiImproveButton text={form.description} field="description" onImproved={(v) => set("description", v)} testid="description-ai-improve" />
                </div>
            </Card>
            <Card title={t("section_appearance")} desc={t("section_appearance_sub")}>
                <div className="grid sm:grid-cols-2 gap-4">
                    <ImageField t={t} label={t("field_logo")} value={form.logo_url} onChange={(v) => set("logo_url", v)} testid="info-logo" ratio="1/1" />
                    <ImageField t={t} label={t("field_hero")} value={form.hero_image_url} onChange={(v) => set("hero_image_url", v)} testid="info-hero" ratio="16/9" />
                </div>
                <div className="mt-4">
                    <Label className="text-sm">{t("field_brand_color")}</Label>
                    <div className="flex gap-2 mt-2 flex-wrap">
                        {ACCENT_PRESETS.map((c) => (
                            <button key={c.value} onClick={() => set("accent_color", c.value)} title={c.name} data-testid={`accent-${c.value}`}
                                className={`w-9 h-9 rounded-full border-2 transition-transform ${form.accent_color === c.value ? "border-foreground scale-110" : "border-white"}`} style={{ background: c.value }} />
                        ))}
                    </div>
                </div>
            </Card>
        </div>
    );
}

function AboutTab({ t, form, set }) {
    const why = form.why_choose_us || [];
    const setWhy = (arr) => set("why_choose_us", arr);
    return (
        <div>
            <Card title={t("section_about_title")}>
                <AreaField label={t("about_text")} value={form.about} onChange={(v) => set("about", v)} rows={5} testid="about-text" />
                <AiImproveButton text={form.about} field="about" onImproved={(v) => set("about", v)} testid="about-ai-improve" />
            </Card>
            <Card title={t("why_title")} desc={t("why_sub")}>
                {why.map((w, i) => (
                    <div key={i} className="mb-3">
                        <div className="flex gap-2">
                            <Input value={w} onChange={(e) => setWhy(why.map((x, j) => j === i ? e.target.value : x))} className="rounded-xl" data-testid={`why-${i}`} />
                            <Button variant="ghost" size="icon" className="rounded-xl shrink-0" onClick={() => setWhy(why.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                        <AiImproveButton text={w} field="why" onImproved={(v) => setWhy(why.map((x, j) => j === i ? v : x))} testid={`why-ai-improve-${i}`} />
                    </div>
                ))}
                <Button variant="ghost" className="glass rounded-full mt-2" onClick={() => setWhy([...why, ""])} data-testid="add-why"><Plus className="w-4 h-4 mr-1" /> {t("add_point")}</Button>
            </Card>
        </div>
    );
}

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
function ContactTab({ t, form, set }) {
    const hours = form.hours && form.hours.length ? form.hours : DAYS.map((d) => ({ day: d, open: "09:00", close: "18:00", closed: false }));
    const setHour = (i, patch) => set("hours", hours.map((h, j) => j === i ? { ...h, ...patch } : h));
    return (
        <div>
            <Card title={t("section_contact")}>
                <div className="grid sm:grid-cols-2 gap-4">
                    <TextField label={t("field_phone")} value={form.phone} onChange={(v) => set("phone", v)} placeholder="+91 90000 00000" testid="contact-phone" />
                    <WhatsAppField label={t("field_whatsapp")} value={form.whatsapp} onChange={(v) => set("whatsapp", v)} testid="contact-whatsapp" />
                    <TextField label={t("field_email")} value={form.email} onChange={(v) => set("email", v)} testid="contact-email" />
                </div>
                <div className="mt-4"><TextField label={t("field_default_wa_msg")} value={form.whatsapp_message} onChange={(v) => set("whatsapp_message", v)} testid="contact-wamsg" /></div>
            </Card>
            <Card title={t("section_location")}>
                <div className="grid sm:grid-cols-2 gap-4">
                    <TextField label={t("field_full_address")} value={form.address} onChange={(v) => set("address", v)} testid="contact-address" />
                    <TextField label={t("field_area")} value={form.area} onChange={(v) => set("area", v)} testid="contact-area" />
                    <TextField label={t("field_city")} value={form.city} onChange={(v) => set("city", v)} testid="contact-city" />
                    <TextField label={t("field_maps_link")} value={form.maps_link} onChange={(v) => set("maps_link", v)} placeholder="https://maps.google.com/?q=..." testid="contact-maps" />
                </div>
            </Card>
            <Card title={t("section_hours")}>
                <div className="space-y-2">
                    {hours.map((h, i) => (
                        <div key={h.day} className="flex items-center gap-2 flex-wrap">
                            <span className="w-24 text-sm">{h.day}</span>
                            {h.closed ? <span className="text-sm text-muted-foreground flex-1">{t("closed")}</span> : (
                                <>
                                    <Input type="time" value={h.open} onChange={(e) => setHour(i, { open: e.target.value })} className="rounded-xl w-32" data-testid={`hour-open-${i}`} />
                                    <span className="text-muted-foreground">–</span>
                                    <Input type="time" value={h.close} onChange={(e) => setHour(i, { close: e.target.value })} className="rounded-xl w-32" data-testid={`hour-close-${i}`} />
                                </>
                            )}
                            <label className="flex items-center gap-1.5 text-xs text-muted-foreground ml-auto">
                                <Switch checked={h.closed} onCheckedChange={(v) => setHour(i, { closed: v })} /> {t("closed")}
                            </label>
                        </div>
                    ))}
                </div>
            </Card>
            <Card title={t("section_social")}>
                <div className="grid sm:grid-cols-2 gap-4">
                    <TextField label={t("field_instagram")} value={form.social_instagram} onChange={(v) => set("social_instagram", v)} testid="social-instagram" />
                    <TextField label={t("field_facebook")} value={form.social_facebook} onChange={(v) => set("social_facebook", v)} testid="social-facebook" />
                    <TextField label={t("field_website")} value={form.social_website} onChange={(v) => set("social_website", v)} testid="social-website" />
                </div>
            </Card>
        </div>
    );
}

function ListTab({ t, kind, form, set, isPro = true, onReachLimit }) {
    const field = kind === "product" ? "products" : "services";
    const items = form[field] || [];
    const setItems = (arr) => set(field, arr);
    const atLimit = kind === "product" && !isPro && items.filter((it) => it.name !== undefined).length >= FREE_PRODUCT_LIMIT;
    const add = () => {
        if (atLimit) { onReachLimit?.(); return; }
        setItems([...items, kind === "product"
            ? { id: uid(), name: "", image_url: "", price: "", description: "", category: "", available: true }
            : { id: uid(), name: "", image_url: "", price: "", description: "" }]);
    };
    const upd = (i, patch) => setItems(items.map((it, j) => j === i ? { ...it, ...patch } : it));
    return (
        <Card title={t(kind === "product" ? "tab_products" : "tab_services")} desc={t(kind === "product" ? "section_products_sub" : "section_services_sub")}>
            {kind === "product" && (
                <div className="mb-4 flex items-center gap-2 text-sm" data-testid="products-plan-status">
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${isPro ? "bg-accent/15 text-accent" : "bg-secondary text-muted-foreground"}`}>
                        {isPro ? "PRO · Unlimited products" : `FREE · ${items.length} / ${FREE_PRODUCT_LIMIT} products used`}
                    </span>
                </div>
            )}
            <div className="space-y-4">
                {items.map((it, i) => (
                    <div key={it.id} className="rounded-2xl border border-border p-4 grid sm:grid-cols-[120px_1fr] gap-4" data-testid={`${kind}-row-${i}`}>
                        <ImageField t={t} label={t("field_name_")} value={it.image_url} onChange={(v) => upd(i, { image_url: v })} testid={`${kind}-img-${i}`} ratio="1/1" />
                        <div className="space-y-2">
                            <div className="grid sm:grid-cols-2 gap-2">
                                <Input value={it.name} onChange={(e) => upd(i, { name: e.target.value })} placeholder={t("field_name_")} className="rounded-xl" data-testid={`${kind}-name-${i}`} />
                                <Input type="number" value={it.price ?? ""} onChange={(e) => upd(i, { price: e.target.value })} placeholder={t("field_price_optional")} className="rounded-xl" data-testid={`${kind}-price-${i}`} />
                            </div>
                            <Textarea value={it.description} onChange={(e) => upd(i, { description: e.target.value })} placeholder={t("field_short_desc_optional")} rows={2} className="rounded-xl" data-testid={`${kind}-desc-${i}`} />
                            <div className="flex items-center justify-between">
                                {kind === "product" ? (
                                    <label className="flex items-center gap-2 text-sm text-muted-foreground"><Switch checked={it.available !== false} onCheckedChange={(v) => upd(i, { available: v })} /> {t("available")}</label>
                                ) : <span />}
                                <Button variant="ghost" size="sm" className="rounded-full text-destructive" onClick={() => setItems(items.filter((_, j) => j !== i))} data-testid={`${kind}-remove-${i}`}><Trash2 className="w-4 h-4 mr-1" /> {t("remove")}</Button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            <Button variant="ghost" className="glass rounded-full mt-4" onClick={add} data-testid={`add-${kind}`}>
                {atLimit ? <Crown className="w-4 h-4 mr-1 text-accent" /> : <Plus className="w-4 h-4 mr-1" />}
                {atLimit ? "Upgrade to add more" : t(kind === "product" ? "add_product" : "add_service")}
            </Button>
        </Card>
    );
}

function GalleryTab({ t, form, set }) {
    const imgs = form.gallery_images || [];
    const [url, setUrl] = useState("");
    const add = (u) => set("gallery_images", [...(form.gallery_images || []), u]);
    return (
        <Card title={t("tab_gallery")} desc={t("section_gallery_sub")}>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                {imgs.map((g, i) => (
                    <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-border group" data-testid={`gallery-img-${i}`}>
                        <img src={g} alt={`gallery ${i}`} className="w-full h-full object-cover" />
                        <button onClick={() => set("gallery_images", imgs.filter((_, j) => j !== i))} className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1.5"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                ))}
            </div>
            <ImageDropzone multiple onUploaded={add} testId="gallery-dropzone" label={t("gallery_drop")} />
            <div className="flex gap-2 mt-3">
                <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder={t("paste_image_url")} className="rounded-xl text-sm" data-testid="gallery-url" />
                <Button type="button" variant="ghost" className="glass rounded-xl shrink-0" onClick={() => { if (url.trim()) { add(url.trim()); setUrl(""); } }} data-testid="gallery-url-add">{t("add")}</Button>
            </div>
        </Card>
    );
}

function ReviewsTab({ t, form, set }) {
    const items = form.testimonials || [];
    const setItems = (arr) => set("testimonials", arr);
    const upd = (i, patch) => setItems(items.map((it, j) => j === i ? { ...it, ...patch } : it));
    return (
        <Card title={t("section_reviews")} desc={t("section_reviews_sub")}>
            <div className="space-y-4">
                {items.map((rev, i) => (
                    <div key={rev.id || i} className="rounded-2xl border border-border p-4" data-testid={`review-row-${i}`}>
                        <div className="grid sm:grid-cols-2 gap-2">
                            <Input value={rev.name} onChange={(e) => upd(i, { name: e.target.value })} placeholder={t("customer_name")} className="rounded-xl" data-testid={`review-name-${i}`} />
                            <div className="flex items-center gap-1">
                                {[1, 2, 3, 4, 5].map((n) => (
                                    <button key={n} onClick={() => upd(i, { rating: n })} data-testid={`review-star-${i}-${n}`}><Star className={`w-5 h-5 ${n <= (rev.rating || 5) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} /></button>
                                ))}
                            </div>
                        </div>
                        <Textarea value={rev.comment} onChange={(e) => upd(i, { comment: e.target.value })} placeholder={t("what_said")} rows={2} className="rounded-xl mt-2" data-testid={`review-comment-${i}`} />
                        <div className="flex items-center justify-between mt-2">
                            {rev.source === "customer"
                                ? <span className="text-[11px] uppercase tracking-wide text-emerald-600 font-medium">Customer</span>
                                : <span />}
                            <Button variant="ghost" size="sm" className="rounded-full text-destructive" onClick={() => setItems(items.filter((_, j) => j !== i))} data-testid={`review-remove-${i}`}><Trash2 className="w-4 h-4 mr-1" /> {t("remove")}</Button>
                        </div>
                    </div>
                ))}
            </div>
            <Button variant="ghost" className="glass rounded-full mt-4" onClick={() => setItems([...items, { id: uid(), name: "", rating: 5, comment: "", avatar_url: "" }])} data-testid="add-review"><Plus className="w-4 h-4 mr-1" /> {t("add_review")}</Button>
        </Card>
    );
}

function SeoTab({ t, form, set }) {
    return (
        <Card title={t("section_seo")} desc={t("section_seo_sub")}>
            <div className="space-y-4">
                <TextField label={t("field_seo_title")} value={form.seo_title} onChange={(v) => set("seo_title", v)} placeholder="Royal Garments | Clothing Store in Jammu" testid="seo-title" />
                <AreaField label={t("field_seo_desc")} value={form.seo_description} onChange={(v) => set("seo_description", v)} rows={2} testid="seo-description" />
            </div>
        </Card>
    );
}

function ShareTab({ t, liveUrl, biz }) {
    const qrRef = useRef(null);
    const copy = () => { navigator.clipboard.writeText(liveUrl); toast.success(t("copied")); api.post(`/public/business/${biz.slug}/event`, { type: "share" }).catch(() => {}); };
    const downloadQR = () => {
        const canvas = qrRef.current?.querySelector("canvas");
        if (!canvas) return;
        const a = document.createElement("a");
        a.href = canvas.toDataURL("image/png");
        a.download = `${biz.slug}-qr.png`;
        a.click();
    };
    const waShare = `https://wa.me/?text=${encodeURIComponent(`Check out my business website: ${liveUrl}`)}`;
    const fbShare = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(liveUrl)}`;
    return (
        <div className="grid md:grid-cols-2 gap-4">
            <Card title={t("section_share_title")} desc={t("section_share_sub")}>
                <div className="flex gap-2">
                    <Input readOnly value={liveUrl} className="rounded-xl" data-testid="share-url" />
                    <Button className="btn-accent rounded-xl shrink-0" onClick={copy} data-testid="share-copy-btn"><Copy className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">{t("copy")}</span></Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                    <a href={waShare} target="_blank" rel="noreferrer"><Button variant="ghost" className="glass rounded-full" data-testid="share-whatsapp-btn"><MessageCircle className="w-4 h-4 mr-1.5" /> WhatsApp</Button></a>
                    <a href={fbShare} target="_blank" rel="noreferrer"><Button variant="ghost" className="glass rounded-full">Facebook</Button></a>
                </div>
            </Card>
            <Card title={t("section_qr_title")} desc={t("section_qr_sub")}>
                <div className="flex items-center gap-5">
                    <div ref={qrRef} className="bg-white p-3 rounded-xl border border-border" data-testid="share-qr">
                        <QRCodeCanvas value={liveUrl} size={140} level="M" includeMargin={false} />
                    </div>
                    <div>
                        <p className="text-sm text-muted-foreground mb-3">{t("scan_to_visit")}</p>
                        <Button className="btn-accent rounded-full" onClick={downloadQR} data-testid="share-qr-download"><Download className="w-4 h-4 mr-1.5" /> {t("download_qr")}</Button>
                    </div>
                </div>
            </Card>
        </div>
    );
}
